import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryConstraint } from "firebase/firestore";
import { Order } from "risa-data-model";
import { FirestoreService } from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";

interface PaOrdersDetails {
  data?: Order[] | null;
  count: number | null;
  loading: boolean;
  countLoading?: boolean;
  error: string | null;
}

const initialState: PaOrdersDetails = {
  data: null,
  loading: false,
  error: null,
  countLoading: false,
  count: null,
};

const paOrdersDetailsSlice = createSlice({
  name: "ordersDetails",
  initialState,
  reducers: {
    fetchPaOrdersStart(state) {
      state.loading = true;
      state.error = null;
    },
    appendPaOrders(state, action: PayloadAction<Order[]>) {
      state.data = state.data
        ? [...state.data, ...action.payload]
        : action.payload;
    },
    fetchPaOrdersSuccess(state, action: PayloadAction<Order[]>) {
      state.data = action.payload;
      state.loading = false;
    },
    fetchPaOrdersFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },

    startFetchingCountsForPaOrders(state) {
      state.countLoading = true;
      state.error = null;
      state.count = 0;
    },
    addCountsForPaOrders(state, action: PayloadAction<number>) {
      state.count = action.payload;
      state.countLoading = false;
    },
    resetPaOrders(state) {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
    fetchSearchedPaOrders(state, action: PayloadAction<Order[]>) {
      state.data = action.payload;
      state.loading = false;
    },
  },
});

export const {
  fetchPaOrdersStart,
  fetchPaOrdersSuccess,
  fetchPaOrdersFailure,
  resetPaOrders,
  appendPaOrders,
  addCountsForPaOrders,
  fetchSearchedPaOrders,
  startFetchingCountsForPaOrders,
} = paOrdersDetailsSlice.actions;

export const fetchPaOrdersFromFirebase =
  (limitingValue?: number, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(fetchPaOrdersStart());
    const unsubscribe =
      FirestoreService.listenToQueryCollectionForFirstBatch<Order>(
        FirestoreCollectionReference.orders(),
        limitingValue || 10,
        (orders) => {
          dispatch(fetchPaOrdersSuccess(orders));
        },
        (error) => {
          dispatch(fetchPaOrdersFailure(error.message));
        },
        ...queryConstraints,
      );

    return unsubscribe;
  };

export const getSearchResultFromFirebaseForPaOrders =
  (...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(fetchPaOrdersStart());
    const unsubscribe = FirestoreService.listenToQueryCollection<Order>(
      FirestoreCollectionReference.orders(),
      (orders) => {
        dispatch(fetchSearchedPaOrders(orders));
        dispatch(fetchPaOrdersCountFromFirebase(...queryConstraints));
      },
      (error) => {
        dispatch(fetchPaOrdersFailure(error.message));
      },
      ...queryConstraints,
    );
    return unsubscribe;
  };

export const fetchNextPaOrderFromFirebase =
  (limitingValue?: number, ...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    const unsubscribe =
      FirestoreService.listenToQueryCollectionForFirstBatch<Order>(
        FirestoreCollectionReference.orders(),
        limitingValue || 10,
        (orders) => {
          dispatch(fetchPaOrdersSuccess(orders));
        },
        (error) => {
          dispatch(fetchPaOrdersFailure(error.message));
        },
        ...queryConstraints,
      );

    return unsubscribe;
  };

export const fetchPaOrdersCountFromFirebase =
  (...queryConstraints: QueryConstraint[]) =>
  async (dispatch) => {
    dispatch(startFetchingCountsForPaOrders());
    const unsubscribe = await FirestoreService.getAllDocumentCount(
      FirestoreCollectionReference.orders(),
      ...queryConstraints,
    );

    dispatch(addCountsForPaOrders(unsubscribe));
    return unsubscribe;
  };

export default paOrdersDetailsSlice.reducer;
